<template>
  <v-col cols="12" lg="8" class="mt-md-1">
    <!-- Active Filter Chips -->
    <ActiveFilterChips
      :active-filters="internalFilters"
      :filter-data="filterData"
      :specialities-list="specialitiesList"
      :priorities-list="prioritiesList"
      :clinics-list="clinicsList"
      :practitioners-list="practitionersList"
      :categories-list="categoriesList"
      :patient-locations-list="patientLocationsList"
      :alert-statuses-list="filterData.alertStatuses || []"
      :alert-types-list="filterData.alertTypes || []"
      :show-practice-switch="true"
      :default-filters="initialFilters"
      @remove-filter="onRemoveFilter"
      @update-filter="onUpdateFilter"
      @clear-all-filters="onClearAllFilters"
    />

    <v-menu
      v-model="showFiltersMenu"
      :close-on-content-click="false"
      :nudge-width="200"
      :disabled="loading"
      offset-x
      max-width="800"
      min-width="600"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          color="primary"
          text
          :disabled="loading"
          :loading="loading"
          v-bind="attrs"
          v-on="on"
        >
          {{ $_t('Filters') }}  
          <v-icon v-if="!loading">mdi-filter</v-icon>
        </v-btn>
      </template>

      <v-card class="filters-popover">
        <v-card-title class="pb-2">
          <span>{{ $_t('Filters') }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="showFiltersMenu = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <DatePicker
                v-model="internalFilters.dateRange"
                @input="onDateRangeChange"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-select
                v-model="internalFilters.appointmentStatuses"
                :items="filterData.appointmentStatuses"
                dense
                :label="$_t('Appointment statuses')"
                item-text="nameWeb"
                item-value="code"
                multiple
                @blur="onAppointmentStatusesChange"
              >
              </v-select>
            </v-col>
            
            <v-col v-if="showCategoryFilter" cols="12" md="6">
              <category-dropdown
                v-model="internalFilters.appointmentCategoryId"
                dense
                :label="$_t('Patient group')"
                @input="onCategoryChange"
              />
            </v-col>
            
            <v-col v-if="showSpecialityFilter" cols="12" md="6">
              <v-select
                v-model="internalFilters.specialityIds"
                :label="$_t('Speciality')"
                :items="specialitiesList"
                item-text="name"
                item-value="id"
                :clearable="true"
                dense
                multiple
                @input="onSpecialityChange"
              ></v-select>
            </v-col>
            
            <v-col v-if="showPriorityFilter" cols="12" md="6">
              <v-select
                v-model="internalFilters.priorityId"
                :label="$_t('Priority')"
                :items="prioritiesList"
                item-text="name"
                item-value="id"
                :clearable="true"
                dense
                @input="onPriorityChange"
              ></v-select>
            </v-col>
            
            <v-col v-if="showClinicFilter" cols="12" md="6">
              <v-select
                v-model="internalFilters.clinicId"
                :label="$_t('Clinic')"
                :items="clinicsList"
                item-text="name"
                item-value="id"
                :clearable="true"
                dense
                @input="onClinicChange"
              ></v-select>
            </v-col>
            
            <v-col v-if="showPractitionerFilter" cols="12" md="6">
              <v-select
                v-model="internalFilters.practitionerId"
                :label="$_t('Practitioner')"
                :items="practitionersList"
                item-text="fullName"
                item-value="id"
                :clearable="true"
                dense
                @input="onPractitionerChange"
              ></v-select>
            </v-col>
            <v-col v-if="showPatientLocationFilter" cols="12" md="6">
              <v-select
                v-model="internalFilters.patientLocationIds"
                :items="patientLocationsList"
                item-text="name"
                item-value="id"
                :label="$_t('Patient location')"
                :multiple="true"
                :clearable="true"
                dense
                @input="onPatientLocationChange"
              ></v-select>
            </v-col>
            <v-col v-if="showAlertStatusFilter" cols="12" md="6">
              <v-select
                v-model="internalFilters.alertStatuses"
                :items="filterData.alertStatuses"
                dense
                :label="$_t('Alert statuses')"
                item-text="name"
                item-value="code"
                multiple
                @blur="onAlertStatusesChange"
              >
              </v-select>
            </v-col>
            <v-col v-if="showAlertTypeFilter" cols="12" md="6">
              <v-select
                v-model="internalFilters.alertTypes"
                :items="filterData.alertTypes"
                dense
                :label="$_t('Alert types')"
                item-text="name"
                item-value="code"
                multiple
                @blur="onAlertTypesChange"
              >
              </v-select>
            </v-col>
          </v-row>
          
          <div v-if="showPracticeSwitch" class="mt-3">
            <v-switch
              class="mb-0"
              v-model="internalFilters.onlyCurrentPractice"
              :label="internalFilters.onlyCurrentPractice ? $_t('All Referrals Within Current Practice') : $_t('My Referrals Only (All practices)')"
              @change="onPracticeSwitchChange"
            />
            <div>
              <span class="text-caption font-weight-light">
                {{ $_t('Toggle On - You can see ALL referrals made within the current practice you are in.') }}
              </span>
            </div>
            <div>
              <span class="text-caption font-weight-light">
                {{ $_t('Toggle Off - You will see only referrals that YOU have made, regardless of which practice you are currently in.') }}
              </span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-menu>
  </v-col>
</template>

<script>
import DatePicker from '@components/common/grid-elements/DatePicker.vue'
import CategoryDropdown from '@components/appointment/AppointmentCategoryDropdown.vue'
import ActiveFilterChips from '@components/common/grid-elements/ActiveFilterChips.vue'
import config from '@src/config'

// Constants for localStorage
const FILTERS_STORAGE_KEY = 'appointmentGridFilters'
const FILTERS_VERSION = '1.0.1'

export default {
  name: 'AppointmentGridFilters',
  components: {
    DatePicker,
    CategoryDropdown,
    ActiveFilterChips
  },
  props: {
    // Filter data from parent
    loading: {
      type: Boolean,
      default: false
    },
    filterData: {
      type: Object,
      default: () => ({
        appointmentStatuses: []
      })
    },
    specialitiesList: {
      type: Array,
      default: () => []
    },
    prioritiesList: {
      type: Array,
      default: () => []
    },
    clinicsList: {
      type: Array,
      default: () => []
    },
    practitionersList: {
      type: Array,
      default: () => []
    },
    categoriesList: {
      type: Array,
      default: () => []
    },
    patientLocationsList: {
      type: Array,
      default: () => []
    },
    initialFilters: {
      type: Object,
      default: () => ({
        dateRange: null,
        appointmentStatuses: [],
        alertStatuses: [],
        alertTypes: [],
        onlyCurrentPractice: true,
        appointmentCategoryId: null,
        specialityIds: [],
        priorityId: null,
        clinicId: null,
        practitionerId: null,
        patientLocationIds: []
      })
    },
    showPracticeSwitch: {
      type: Boolean,
      default: () => config.showPracticeSwitch
    },
    showCategoryFilter: {
      type: Boolean,
      default: false
    },
    showSpecialityFilter: {
      type: Boolean,
      default: false
    },
    showPriorityFilter: {
      type: Boolean,
      default: false
    },
    showClinicFilter: {
      type: Boolean,
      default: false
    },
    showPractitionerFilter: {
      type: Boolean,
      default: false
    },
    showPatientLocationFilter: {
      type: Boolean,
      default: false
    },
    showAlertStatusFilter: {
      type: Boolean,
      default: true
    },
    showAlertTypeFilter: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      internalFilters: { ...this.initialFilters },
      showFiltersMenu: false
    }
  },
  watch: {
    initialFilters: {
      handler(newFilters) {
        this.internalFilters = { ...newFilters }
      },
      deep: true
    },

  },
  mounted() {
    this.loadFiltersFromStorage()
  },
  methods: {
    saveFiltersToStorage() {
      try {
        const filtersData = {
          version: FILTERS_VERSION,
          filters: {
            appointmentStatuses: this.internalFilters.appointmentStatuses,
            alertStatuses: this.internalFilters.alertStatuses,
            alertTypes: this.internalFilters.alertTypes,
            onlyCurrentPractice: this.internalFilters.onlyCurrentPractice,
            appointmentCategoryId: this.internalFilters.appointmentCategoryId,
            specialityIds: this.internalFilters.specialityIds,
            priorityId: this.internalFilters.priorityId,
            clinicId: this.internalFilters.clinicId,
            practitionerId: this.internalFilters.practitionerId,
            patientLocationIds: this.internalFilters.patientLocationIds
          },
          timestamp: new Date().toISOString()
        }
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersData))
      } catch (error) {
        console.error('Error saving filters to localStorage:', error)
      }
    },

    loadFiltersFromStorage() {
      try {
        const savedData = localStorage.getItem(FILTERS_STORAGE_KEY)
        if (!savedData) {
          return
        }

        const parsedData = JSON.parse(savedData)
        
        const migratedData = this.migrateFiltersVersion(parsedData)
        
        if (migratedData && migratedData.filters) {
          // Merge saved filters with initial filters (to ensure all fields exist)
          this.internalFilters = {
            ...this.initialFilters,
            ...migratedData.filters
          }
          
          // Emit the loaded filters to parent
          this.$emit('filters-changed', { ...this.internalFilters })
        }
      } catch (error) {
        console.error('Error loading filters from localStorage:', error)
        localStorage.removeItem(FILTERS_STORAGE_KEY)
      }
    },
    migrateFiltersVersion(data) {
      if (!data) {
        return null
      }
      // version handling
      if (data.version !== FILTERS_VERSION) {
        console.error(`Filter version mismatch. Saved: ${data.version}, Current: ${FILTERS_VERSION}. Using default filters.`)
        // Clear the outdated data from storage
        this.clearFiltersFromStorage()
        return null
      }

      return data
    },
    clearFiltersFromStorage() {
      try {
        localStorage.removeItem(FILTERS_STORAGE_KEY)
      } catch (error) {
        console.error('Error clearing filters from localStorage:', error)
      }
    },

    onFilterChange(filterKey, value) {
      this.internalFilters[filterKey] = value
      this.emitFiltersChanged()
    },
    onDateRangeChange(value) {
      this.onFilterChange('dateRange', value)
    },
    
    onAppointmentStatusesChange() {
      this.emitFiltersChanged()
    },
    onAlertStatusesChange() {
      this.emitFiltersChanged()
    },
    onAlertTypesChange() {
      this.emitFiltersChanged()
    },
    
    onPracticeSwitchChange(value) {
      this.onFilterChange('onlyCurrentPractice', value)
    },
    
    onCategoryChange(value) {
      this.onFilterChange('appointmentCategoryId', value)
    },
    
    onSpecialityChange(value) {
      this.onFilterChange('specialityIds', value)
    },
    
    onPriorityChange(value) {
      this.onFilterChange('priorityId', value)
    },
    
    onClinicChange(value) {
      this.onFilterChange('clinicId', value)
    },
    
    onPractitionerChange(value) {
      this.onFilterChange('practitionerId', value)
    },
    
    onPatientLocationChange(value) {
      this.onFilterChange('patientLocationIds', value)
    },
    
    applyFilters() {
      this.emitFiltersChanged()
      this.showFiltersMenu = false
    },
    emitFiltersChanged() {
      this.$emit('filters-changed', { ...this.internalFilters })
      // Automatically save to localStorage whenever filters change
      this.saveFiltersToStorage()
    },
    getCurrentFilters() {
      return { ...this.internalFilters }
    },
    resetFilters() {
      this.internalFilters = { ...this.initialFilters }
      this.emitFiltersChanged()
    },
    updateFilter(filterKey, value) {
      this.onFilterChange(filterKey, value)
    },
    
    // Event handlers for ActiveFilterChips
    onRemoveFilter(filterKey) {
      // Reset the filter to its default value
      const defaultValue = this.initialFilters[filterKey]
      this.internalFilters[filterKey] = defaultValue
      this.emitFiltersChanged()
    },
    
    onUpdateFilter(filterKey, value) {
      this.internalFilters[filterKey] = value
      this.emitFiltersChanged()
    },
    
    onClearAllFilters() {
      // Preserve the date range but clear all other filters to their default values
      const currentDateRange = this.internalFilters.dateRange
      const currentOnlyCurrentPractice = this.internalFilters.onlyCurrentPractice
      
      this.internalFilters = {
        dateRange: currentDateRange,
        appointmentStatuses: [],
        onlyCurrentPractice: currentOnlyCurrentPractice,
        appointmentCategoryId: null,
        specialityIds: [],
        priorityId: null,
        clinicId: null,
        practitionerId: null,
        patientLocationIds: [],
        alertStatuses: [],
        alertTypes: []
      }
      this.emitFiltersChanged()
    },
  }
}
</script>

<style scoped>

</style>
